import { Style, StyleSheet, StyleWrapper } from "@design-system/style-sheet"
import { Theme } from "@design-system/themes"
import { NamedStyles } from "../types"
import { createStyleSheet, compileCSS } from "./style-sheet"

export type StylesFunc<T> = (theme: Theme) => T
export type StylesResult<T> = { [name in keyof T]: StyleWrapper<T[name]> }
export type CSSResult<T> = { [name in keyof T]: string }

const visuallyStyles = createStyleSheet({
  style: {
    borderWidth: "0",
    clip: "rect(1px, 1px, 1px, 1px)",
    height: "1px",
    overflow: "hidden",
    padding: "0",
    position: "absolute",
    width: "1px",
  },
})

const visuallyHidden = visuallyStyles.style

export type ThemeListener = (theme: Theme) => void

export class Themes {
  theme: Theme
  absoluteFill: typeof StyleSheet.absoluteFill
  absoluteFillObject: typeof StyleSheet.absoluteFillObject
  flatten: typeof StyleSheet.flatten
  hairlineWidth: typeof StyleSheet.hairlineWidth
  compose: typeof StyleSheet.compose
  visuallyHidden: typeof visuallyHidden
  themeChangeListeners: Set<ThemeListener>

  constructor(theme: Theme) {
    this.theme = theme
    this.absoluteFill = StyleSheet.absoluteFill
    this.absoluteFillObject = StyleSheet.absoluteFillObject
    this.flatten = StyleSheet.flatten
    this.hairlineWidth = StyleSheet.hairlineWidth
    this.compose = StyleSheet.compose
    this.visuallyHidden = visuallyHidden
    this.themeChangeListeners = new Set()

    this._evaluateStyleDefinitions = this._evaluateStyleDefinitions.bind(this)
    this._evaluateCSS = this._evaluateCSS.bind(this)
  }

  setTheme(theme: Theme) {
    this.theme = theme
    this.notifyThemeChange(theme)
  }

  notifyThemeChange(theme: Theme) {
    this.themeChangeListeners.forEach((listener) => listener(theme))
  }

  onThemeChange(listener: ThemeListener) {
    this.themeChangeListeners.add(listener)
    return () => this.removeChangeListener(listener)
  }

  removeChangeListener(listener: ThemeListener) {
    this.themeChangeListeners.delete(listener)
  }

  _createWithTheme<T, R>(input: T, evaluate: (input: T) => R) {
    const map = new Map()
    const originalInput = input
    const styleDefinitions = evaluate(originalInput)
    map.set(this.theme.key, styleDefinitions)
    const result = {} as R
    const props = Object.keys(styleDefinitions).reduce((acc, key) => {
      acc[key] = {
        enumerable: true,
        get: () => {
          // make sure we can use the new theme after aplied a new theme
          const themeKey = this.theme.key
          if (!map.has(themeKey)) {
            map.set(themeKey, evaluate(originalInput))
          }
          const styles = map.get(themeKey) || {}
          return styles[key]
        },
      }
      return acc
    }, {})

    Object.defineProperties(result, props)

    return result
  }

  _evaluateStyleDefinitions<T extends NamedStyles<T>>(
    styles: T | StylesFunc<T>,
  ): StylesResult<T> {
    let stylesWithTheme: T
    if (typeof styles === "function") {
      stylesWithTheme = (styles as StylesFunc<T>)(this.theme)
    } else {
      stylesWithTheme = styles
    }

    if (process.env.NODE_ENV !== "production") {
      const validate = require("./validate")
      stylesWithTheme = validate.filter(stylesWithTheme)
    }

    return createStyleSheet(stylesWithTheme) as {
      [name in keyof T]: StyleWrapper<T[name]>
    }
  }

  create<T extends NamedStyles<T>>(styles: T | StylesFunc<T>): StylesResult<T> {
    return this._createWithTheme<T | StylesFunc<T>, StylesResult<T>>(
      styles,
      this._evaluateStyleDefinitions,
    )
  }

  _evaluateCSS<T extends { [key: string]: Style }>(
    styles: T | StylesFunc<T>,
  ): CSSResult<T> {
    let stylesWithTheme: T
    if (typeof styles === "function") {
      stylesWithTheme = (styles as StylesFunc<T>)(this.theme)
    } else {
      stylesWithTheme = styles
    }

    if (process.env.NODE_ENV !== "production") {
      const validate = require("./validate")
      stylesWithTheme = validate.filter(stylesWithTheme)
    }

    return compileCSS(stylesWithTheme) as CSSResult<T>
  }

  createCSS<T extends { [key: string]: Style }>(styles: T | StylesFunc<T>) {
    return this._createWithTheme<T | StylesFunc<T>, CSSResult<T>>(
      styles,
      this._evaluateCSS,
    )
  }
}
